import { Sensor } from '@models';
import { SensorReadingInfo } from '@models/EventLog/SensorReadingInfo.model';
import { GpsInfo } from '@models/Map/gpsInfo.model';

// Reading type names that carry GPS coordinates. Hosts that plot readings on a map
// always load these alongside the user's reading types, so the pairing below works
// even when the user did not check latitude/longitude themselves.
export const LATITUDE_TYPES = ['latitude', 'lat'];
export const LONGITUDE_TYPES = ['longitude', 'long', 'lng'];


export function isGpsReadingType(typeName: string): boolean {
  const type = (typeName ?? '').toLowerCase();
  return LATITUDE_TYPES.includes(type) || LONGITUDE_TYPES.includes(type);
}


function hasGps(gps: GpsInfo | undefined): boolean {
  return gps != undefined && (Math.abs(gps.gpsLat) > 0 || Math.abs(gps.gpsLong) > 0);
}


// Resolve each reading's GPS by priority and tag it with gpsSource:
//   1. latitude/longitude reading types (same sensor + timestamp) -> 'readings'
//   2. the reading's own gpsLocation           -> 'reading'
//   3. the owning sensor's gpsLocation          -> 'sensor'
//   4. none                                     -> 'none'
// Coordinates from sources 1 and 3 are written onto the reading so a map can plot them.
// Latitude/longitude pair within a sensor only. Same rules as Analyze Sensor Readings.
export function backfillGpsLocations(readings: SensorReadingInfo[], sensors: Sensor[]): void {
  if (readings == undefined || readings.length === 0) {
    return;
  }

  // Index lat/long values by "<uuid>|<timestampMs>" so they only pair within a sensor.
  const latByKey = new Map<string, number>();
  const longByKey = new Map<string, number>();

  readings.forEach(r => {
    const type = (r.type != undefined) ? r.type.toLowerCase() : '';
    const value = Number(r.value);
    if (isNaN(value)) {
      return;
    }
    const key = r.uuid + '|' + new Date(r.createdDate).getTime();
    if (LATITUDE_TYPES.includes(type)) {
      latByKey.set(key, value);
    } else if (LONGITUDE_TYPES.includes(type)) {
      longByKey.set(key, value);
    }
  });

  // Sensor gpsLocation by uuid, for source #3.
  const sensorGpsByUuid = new Map<string, GpsInfo>();
  (sensors ?? []).forEach(s => {
    if (hasGps(s.gpsLocation)) {
      sensorGpsByUuid.set(s.uuid, s.gpsLocation);
    }
  });

  readings.forEach(r => {
    // 1. latitude/longitude reading types
    const key = r.uuid + '|' + new Date(r.createdDate).getTime();
    const lat = latByKey.get(key);
    const long = longByKey.get(key);
    if (lat != undefined || long != undefined) {
      r.gpsLocation = { id: 0, gpsLat: lat ?? 0, gpsLong: long ?? 0, gpsAlt: 0, locationDescription: '' };
      r.gpsSource = 'readings';
      return;
    }

    // 2. the reading's own gpsLocation
    if (hasGps(r.gpsLocation)) {
      r.gpsSource = 'reading';
      return;
    }

    // 3. the owning sensor's gpsLocation
    const sensorGps = sensorGpsByUuid.get(r.uuid);
    if (sensorGps != undefined) {
      r.gpsLocation = { ...sensorGps };
      r.gpsSource = 'sensor';
      return;
    }

    // 4. none
    r.gpsSource = 'none';
  });
}
